import { Router, ActivatedRouteSnapshot, UrlTree, createUrlTreeFromSnapshot } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { AppStateService } from '../services/app-state/app-state.service';
import { ProjectService } from '../services/project/project.service';
import { Project, Version, Workflow } from '../interfaces/project.interface';
import { User } from '../interfaces/user.interface';
import { PermissionId } from '../constants/permissions.constants';
import { LocalstorageHelper } from '../helpers/localstorage.helper';
import {
  getMostRecentPublishedVersion,
  getVisibleVersionsForRole,
  hasPublishedVersions as hasPublishedProjectVersions,
  isOperatorRole,
  NO_PUBLISHED_VERSIONS_MESSAGE,
} from '../helpers/version-visibility.helper';

/**
 * Ensures user is loaded in AppState
 */
export async function ensureUserLoaded(appStateService: AppStateService): Promise<User | undefined> {
  if (appStateService.user() === undefined) {
    await appStateService.loadUser();
  }
  return appStateService.user();
}

/**
 * Ensures the project list in AppState includes data needed for the route.
 * Reloads from the API when the list is empty or when the target project id
 * is missing (stale list right after creation).
 *
 * @returns true if `loadProjects()` was invoked so callers can avoid an
 * immediate duplicate list fetch in `findProject`.
 */
export async function ensureProjectsLoaded(
  appStateService: AppStateService,
  routeProjectId?: number,
): Promise<boolean> {
  const projects = appStateService.projects();
  if (projects.length === 0) {
    try {
      await appStateService.loadProjects();
      return true;
    } catch (error) {
      console.warn('Failed to load projects list in guard:', error);
      return false;
    }
  }

  if (
    routeProjectId != null &&
    routeProjectId > 0 &&
    !Number.isNaN(routeProjectId) &&
    !appStateService.getProjectById(routeProjectId)
  ) {
    try {
      await appStateService.loadProjects();
      return true;
    } catch (error) {
      console.warn('Failed to load projects list in guard:', error);
      return false;
    }
  }
  return false;
}

/**
 * Finds a project either from state, storage, or by reloading
 * Always refreshes project data from API to ensure we have the latest versions
 * The debouncing in refreshProjectData() prevents duplicate API calls during the same navigation
 *
 * @param listAlreadyFetchedInGuard pass true when `ensureProjectsLoaded` just called
 * `loadProjects()` for this navigation so we do not fetch the list again back-to-back.
 */
export async function findProject(
  appStateService: AppStateService,
  projectId: number,
  listAlreadyFetchedInGuard = false,
): Promise<Project | undefined> {
  let project: Project | undefined = appStateService.getProjectById(projectId);

  if (!project) {
    const restored = await appStateService.tryRestoreProjectFromStorage(projectId);
    if (restored) {
      project = appStateService.project();
    }
  }

  if (!project && !listAlreadyFetchedInGuard) {
    try {
      await appStateService.loadProjects();
      project = appStateService.getProjectById(projectId);
    } catch (error) {
      console.error(`Failed to fetch project with ID ${projectId}:`, error);
    }
  }

  // List + storage can lag right after project creation; single-project GET is authoritative.
  // Use refreshProjectData(force) so we do one GET here; the follow-up refresh below debounces
  // within 1s and avoids a second GET to the same endpoint.
  if (!project) {
    try {
      const refreshed = await appStateService.refreshProjectData(projectId, true);
      if (refreshed?.projectId === projectId) {
        appStateService.upsertProjectInList(refreshed);
        project = refreshed;
      }
    } catch (error) {
      console.error(`Failed to fetch project with ID ${projectId} by single-project API:`, error);
    }
  }

  // Always refresh to ensure we have the latest versions.
  // refreshProjectData() has a 1s debounce to prevent duplicate calls.
  if (project) {
    try {
      await appStateService.refreshProjectData(projectId);
      // refreshProjectData() updates project() signal via setProject(), not the projects() array
      project = appStateService.project();
    } catch (error) {
      console.warn('Failed to refresh project data, using cached data:', error);
    }
  }

  return project;
}

const NO_ACCESSIBLE_VERSION_MESSAGE = 'No accessible version found for this project';

/**
 * Resolves the best fallback version when the desired one is unavailable.
 * Priority: currentVersionId match → most recent published → first visible.
 */
function resolveFallbackVersion(
  visibleVersions: Version[],
  currentVersionId: number | undefined,
): Version | undefined {
  return (
    (currentVersionId != null && visibleVersions.find((v) => v.versionId === currentVersionId)) ||
    getMostRecentPublishedVersion(visibleVersions) ||
    visibleVersions[0]
  );
}

/**
 * Redirects to the fallback version URL, or to /projects if no fallback exists.
 */
function redirectToFallbackOrProjects(
  fallbackVersion: Version | undefined,
  toastrService: ToastrService,
  router: Router,
  route: ActivatedRouteSnapshot,
): UrlTree {
  if (!fallbackVersion) {
    toastrService.error(NO_ACCESSIBLE_VERSION_MESSAGE);
    return router.parseUrl('/projects');
  }
  return createUrlTreeFromSnapshot(route, [], {
    ...route.queryParams,
    versionId: fallbackVersion.versionId,
  });
}

/**
 * Handles version selection and permissions
 */
export async function handleVersion(
  projectService: ProjectService,
  appStateService: AppStateService,
  toastrService: ToastrService,
  router: Router,
  route: ActivatedRouteSnapshot,
  project: Project,
  versionId: number,
  user: User,
): Promise<boolean | UrlTree> {
  const allVersions = project.versions ?? [];
  const currentRole = projectService.getCurrentUserRoleFromProject(project);
  const visibleVersions = getVisibleVersionsForRole(allVersions, currentRole);
  const operatorHasNoPublishedVersions =
    isOperatorRole(currentRole) && !hasPublishedProjectVersions(allVersions);

  if (operatorHasNoPublishedVersions) {
    toastrService.error(NO_PUBLISHED_VERSIONS_MESSAGE);
    return router.parseUrl('/projects');
  }

  if (isNaN(versionId)) {
    // No versionId in URL — try the last-used version from localStorage (visible only)
    const lastInteractedVersionId = LocalstorageHelper.getLastInteractedVersion(project.projectId);
    if (lastInteractedVersionId) {
      const lastInteractedVersion = visibleVersions.find(
        (version) => version.versionId === lastInteractedVersionId,
      );
      if (lastInteractedVersion) {
        return createUrlTreeFromSnapshot(route, [], {
          ...route.queryParams,
          versionId: lastInteractedVersion.versionId,
        });
      }
      // Stored version no longer exists or is no longer visible for current role
      LocalstorageHelper.removeLastInteractedVersion(project.projectId);
    }

    return redirectToFallbackOrProjects(
      resolveFallbackVersion(visibleVersions, project.currentVersionId),
      toastrService, router, route,
    );
  }

  const selectedVersion = allVersions.find((version) => version.versionId === versionId);
  if (!selectedVersion) {
    return redirectToFallbackOrProjects(
      resolveFallbackVersion(visibleVersions, project.currentVersionId),
      toastrService, router, route,
    );
  }

  const selectedVersionIsVisible = visibleVersions.some(
    (visibleVersion) => visibleVersion.versionId === selectedVersion.versionId,
  );
  if (!selectedVersionIsVisible) {
    return redirectToFallbackOrProjects(
      resolveFallbackVersion(visibleVersions, undefined),
      toastrService, router, route,
    );
  }

  const userHasPermission = projectService.getUserHasPermissionForProject(
    project,
    user,
    PermissionId.READ,
  );
  if (!userHasPermission) {
    toastrService.error('User does not have permission to view this version');
    return router.parseUrl('/projects');
  }

  return true;
}

/**
 * Gets the full version from the server
 */
export async function getFullVersion(
  projectService: ProjectService,
  router: Router,
  project: Project,
  versionId: number,
  options?: { forceRefresh?: boolean },
): Promise<Version | undefined> {
  try {
    const fullVersion = await firstValueFrom(
      projectService.getFullVersion(project.projectId, versionId, options)
    );

    if (!fullVersion) {
      console.error(`Full version ${versionId} not found in project ${project.projectId}`);
      return undefined;
    }

    return fullVersion as Version;
  } catch (error) {
    console.error('Error loading full version:', error);
    return undefined;
  }
}

/**
 * Handles notebook and workflow selection
 */
export async function handleNotebookAndWorkflow(
  appStateService: AppStateService,
  router: Router,
  route: ActivatedRouteSnapshot,
  fullVersion: Version
): Promise<boolean | UrlTree> {
  // Check both route params and query params for workflowId
  const workflowIdFromRoute = Number(route.params['workflowId']);
  const workflowIdFromQuery = Number(route.queryParams['workflowId']);
  const workflowId = !isNaN(workflowIdFromRoute) ? workflowIdFromRoute : workflowIdFromQuery;

  // Handle workflows (API v0.3.0 - workflows only)
  if (fullVersion.workflows && fullVersion.workflows.length > 0) {
    const workflowExists = !isNaN(workflowId)
      ? fullVersion.workflows.some(w => w.workflowId === workflowId)
      : false;

    // ✅ If workflowId is missing OR invalid for this version, redirect to the best matching workflow
    // (by current workflow name, defaultWorkflowId, or first workflow).
    if (isNaN(workflowId) || !workflowExists) {
      const workflow = findWorkflow(appStateService, fullVersion, workflowId);

      if (workflow) {
        return createUrlTreeFromSnapshot(route, [], {
          ...route.queryParams,
          workflowId: workflow.workflowId,
        });
      }
    }
  }

  return true;
}

/**
 * Finds a workflow from the version
 */
export function findWorkflow(
  appStateService: AppStateService,
  fullVersion: Version,
  workflowId: number | string
): Workflow | undefined {
  // If workflowId is provided, find by ID
  if (!isNaN(Number(workflowId))) {
    const foundById = fullVersion.workflows?.find(
      (w) => w.workflowId === Number(workflowId)
    );
    if (foundById) return foundById;
  }

  // Try to find by name from current workflow
  const currentWorkflowName = appStateService.workflow()?.name;
  if (currentWorkflowName) {
    const foundByName = fullVersion.workflows?.find(
      (w) => w.name === currentWorkflowName
    );
    if (foundByName) return foundByName;
  }

  // Check for default workflow
  if (fullVersion.defaultWorkflowId && fullVersion.workflows) {
    const defaultWorkflow = fullVersion.workflows.find(
      (w) => w.workflowId === fullVersion.defaultWorkflowId
    );
    if (defaultWorkflow) return defaultWorkflow;
  }

  // Default to the first workflow
  return fullVersion.workflows && fullVersion.workflows.length > 0
    ? fullVersion.workflows[0]
    : undefined;
}

